import { logEvent } from "firebase/analytics";
import { getFirebaseAnalytics } from "./firebase.client";

// Custom event types for better type safety
export interface CustomAnalyticsEvent {
  // Page events
  page_view: {
    page_path: string;
    page_title?: string;
  };

  // User interaction events
  search: {
    search_term: string;
    results_count?: number;
  };

  download: {
    file_name: string;
    file_type?: string;
    component?: string;
  };

  share: {
    content_type: string;
    content_id: string;
    method?: string;
  };
}

// Type-safe analytics tracking function
export const trackEvent = async <T extends keyof CustomAnalyticsEvent>(eventName: T, parameters: CustomAnalyticsEvent[T]): Promise<void> => {
  try {
    const analytics = await getFirebaseAnalytics();
    if (analytics) {
      logEvent(analytics, eventName as string, parameters as Record<string, unknown>);
      console.log(`Analytics event tracked: ${eventName}`, parameters);
    }
  } catch (error) {
    console.error(`Error tracking analytics event ${eventName}:`, error);
  }
};

// Common tracking functions for easy use
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  return trackEvent("page_view", {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  return trackEvent("search", {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

export const trackDownload = (fileName: string, fileType?: string, component?: string) => {
  return trackEvent("download", {
    file_name: fileName,
    file_type: fileType,
    component: component,
  });
};

export const trackShare = (contentType: string, contentId: string, method?: string) => {
  return trackEvent("share", {
    content_type: contentType,
    content_id: contentId,
    method,
  });
};

// Hook for analytics in React components
export const useAnalytics = () => {
  return {
    trackEvent,
    trackPageView,
    trackSearch,
    trackDownload,
    trackShare,
  };
};
